import React, { useState, useEffect } from "react";
import {
  Table,
  InputNumber,
  Input,
  Select,
  Popconfirm,
  Button,
  Drawer,
} from "antd";
import { FormattedMessage, useIntl } from "react-intl";
import {
  DeleteOutlined,
  MinusCircleOutlined,
  PlusCircleOutlined,
  PlusOutlined,
} from "@ant-design/icons";
import { useDispatch, useSelector } from "react-redux";
import { getProductsServicesAPI } from "features/ProductsandServices/ProductsandServicesAPI";
import AddProduct from "features/ProductsandServices/Components/AddProduct";

function ProductServiceTable({
  setFormattedSubTotal,
  setFormattedGrandTotal,
  discount,
  discountType,
  initialProducts,
  selectedProductss,
  setSelectedProductss,
}) {
  const intl = useIntl();
  const dispatch = useDispatch();
  const productsList = useSelector(
    (state) => state.productsServices.productsList
  );

  const [rowKey, setRowKey] = useState(selectedProductss.length + 1);
  const [open, setOpen] = useState(false);
  const [newProduct, setNewProduct] = useState(null);
  const [currentKey, setCurrentKey] = useState(null);

  useEffect(() => {
    dispatch(
      getProductsServicesAPI({ pageNo: null, pageSize: null, searchQuery: "" })
    );
  }, []);

  const calculateAmount = (quantity, unitPrice, discount = 0) => {
    const amount = quantity * unitPrice;
    const subtotal = amount;
    const discountAmount = subtotal * (discount / 100);
    return subtotal - discountAmount;
  };

  const calculateSubTotalAndGrandTotal = (products) => {
    const validProducts = products.filter((p) => p.productId);

    const subTotal = validProducts.reduce((total, product) => {
      const amount = calculateAmount(
        product.quantity || 1,
        product.unitPrice || 0,
        product.discount || 0
      );
      return total + amount;
    }, 0);

    const discountAmount =
      discountType === 1 ? (subTotal * discount) / 100 : Number(discount);

    const grandTotal = Math.max(0, subTotal - discountAmount);

    setFormattedSubTotal(subTotal.toFixed(2));
    setFormattedGrandTotal(grandTotal.toFixed(2));
  };

  const handleProductSelect = (productId, rowKeyToUpdate) => {
    const product = productsList.items.find((p) => p.id === productId);
    if (!product) return;

    const updatedProducts = selectedProductss.map((item) => {
      if (item.key === rowKeyToUpdate) {
        if (item.productId === product.id) return item;

        const amount = calculateAmount(1, product.salePrice, 0);
        return {
          ...item,
          id: 0,
          productId: product.id,
          name: product.name,
          description: product.description || "",
          quantity: 1,
          unitPrice: Number(product.salePrice) || 0,
          discount: 0,
          amount: amount,
          isActive: true,
        };
      }
      return item;
    });

    const selectedRow = selectedProductss.find(
      (item) => item.key === rowKeyToUpdate
    );

    const isLastRow =
      selectedRow &&
      rowKeyToUpdate === selectedProductss[selectedProductss.length - 1].key;
    const wasEmptyRow = selectedRow && selectedRow.productId === null;

    if (isLastRow && wasEmptyRow && productId) {
      const newRow = {
        key: rowKey + 1,
        id: 0,
        productId: null,
        name: "",
        description: "",
        quantity: 1,
        unitPrice: 0,
        discount: 0,
        amount: 0,
        isActive: true,
      };
      updatedProducts.push(newRow);
      setRowKey((prevKey) => prevKey + 1);
    }

    setSelectedProductss(updatedProducts);
    calculateSubTotalAndGrandTotal(updatedProducts);
  };

  const handleQuantityChange = (value, key) => {
    const updated = selectedProductss.map((item) =>
      item.key === key
        ? {
            ...item,
            quantity: value,
            amount: calculateAmount(value, item.unitPrice, item.discount),
          }
        : item
    );
    setSelectedProductss(updated);
    calculateSubTotalAndGrandTotal(updated);
  };

  const handleDiscountChange = (value, key) => {
    const updated = selectedProductss.map((item) =>
      item.key === key
        ? {
            ...item,
            discount: value || 0,
            amount: calculateAmount(item.quantity, item.unitPrice, value || 0),
          }
        : item
    );
    setSelectedProductss(updated);
    calculateSubTotalAndGrandTotal(updated);
  };

  const handleDelete = (key) => {
    const newProducts = selectedProductss.filter((item) => item.key !== key);
    setSelectedProductss(newProducts);
    calculateSubTotalAndGrandTotal(newProducts);
  };
  const getProductOptions = (record) => {
    // Step 1: Base filtering — exclude products already selected in other rows
    let filteredProducts = productsList?.items?.filter(
      (product) =>
        !selectedProductss.some(
          (item) => item.productId === product.id && item.key !== record.key
        )
    );

    // Step 2: Handle special case when record.productId is null
    if (record.productId == null) {
      const matchingItem = selectedProductss.find(
        (item) => item.key === record.key && item.productId != null
      );

      if (matchingItem) {
        // Remove duplicate null-entry for same row
        const updatedProducts = selectedProductss.map((item) => {
          if (item.key === record.key && item.productId == null) {
            return {
              ...item,
              key: 999, // update key to 999
            };
          }
          return item;
        });
        setSelectedProductss(updatedProducts);
        // Exclude the already selected productId for this record from the options
        filteredProducts = filteredProducts?.filter(
          (product) => product.id !== matchingItem.productId
        );
      }
    }

    // Step 3: Return final options
    return filteredProducts?.map((product) => ({
      value: product.id,
      label: `${product.id} | ${product.name}`,
    }));
  };

  const showDrawer = () => {
    setOpen(true);
  };
  const onClose = () => {
    setOpen(false);
  };

  const columns = [
    {
      title: "",
      key: "action",
      width: 32,
      render: (text, record, index) => {
        const isLastRow = index === selectedProductss.length - 1;
        const isProductSelected = !!record.productId;

        if (index === 0 && !record.productId) {
          return null;
        }

        if (isLastRow && isProductSelected) {
          return (
            <span
              onClick={() => {
                const newRow = {
                  key: rowKey,
                  id: 0,
                  productId: null,
                  name: "",
                  description: "",
                  quantity: 1,
                  unitPrice: 0,
                  discount: 0,
                  amount: 0,
                  isActive: true,
                };
                setSelectedProductss([...selectedProductss, newRow]);
                setRowKey((prev) => prev + 1);
              }}
              style={{ color: "green", fontSize: "18px", cursor: "pointer" }}
            >
              <PlusCircleOutlined />
            </span>
          );
        }

        return (
          <Popconfirm
            title={
              <FormattedMessage id="AddEstimate.Areyousureyouwanttoremovethisitem" />
            }
            okText={<FormattedMessage id="Common.Yes" />}
            cancelText={<FormattedMessage id="Common.No" />}
            onConfirm={() => handleDelete(record.key)}
          >
            <span
              style={{
                color: "red",
                fontSize: "18px",
                cursor: "pointer",
                fontWeight: "bold",
              }}
            >
              <MinusCircleOutlined />
            </span>
          </Popconfirm>
        );
      },
    },
    {
      title: <FormattedMessage id="AddEstimate.ProductService" />,
      dataIndex: "name",
      key: "name",
      width: 250,
      render: (text, record) => (
        <Select
          size="middle"
          showSearch
          value={record.productId || undefined}
          placeholder={intl.formatMessage({
            id: "AddEstimate.placeholder.SelectForProductService",
          })}
          optionFilterProp="label"
          onSelect={(value) => handleProductSelect(value, record.key)}
          className="w-100"
          // options={productsList?.items?.map((product) => ({
          //   value: product.id,
          //   label: `${product.id} | ${product.name}`,
          //   disabled: selectedProductss.some(
          //     (item) => item.productId === product.id && item.key !== record.key
          //   ),
          // }))}
          options={getProductOptions(record)}
          dropdownRender={(menu) => (
            <>
              {menu}
              <div style={{ padding: 8 }}>
                <Button
                  type="link"
                  icon={<PlusOutlined />}
                  onClick={() => {
                    setCurrentKey(record.key);
                    showDrawer();
                  }}
                  style={{ padding: 0 }}
                >
                  {intl.formatMessage({
                    id: "AddEstimate.button.AddNewProduct",
                  })}
                </Button>
              </div>
            </>
          )}
        />
      ),
    },
    {
      title: <FormattedMessage id="AddEstimate.Description" />,
      dataIndex: "description",
      key: "description",
      render: (text, record) => (
        <Input.TextArea
          size="middle"
          rows={1}
          value={record.description}
          placeholder={intl.formatMessage({
            id: "Quote.Placeholder.EnterDescription",
          })}
          onChange={(e) => {
            const newValue = e.target.value;
            setSelectedProductss((prev) =>
              prev.map((item) =>
                item.key === record.key
                  ? { ...item, description: newValue }
                  : item
              )
            );
          }}
        />
      ),
    },
    {
      title: "Qty/hrs",
      dataIndex: "quantity",
      key: "quantity",
      align: "center",
      width: 89,
      render: (text, record) => (
        <InputNumber
          size="middle"
          min={1}
          controls={false}
          value={record.quantity}
          onChange={(value) => {
            const safeValue = value && value >= 1 ? value : 1;
            handleQuantityChange(safeValue, record.key);
          }}
          onBlur={(e) => {
            const inputValue = e.target.value?.trim();
            const parsed = parseInt(inputValue);
            if (!parsed || parsed < 1) {
              handleQuantityChange(1, record.key);
            }
          }}
          onKeyDown={(e) => {
            if (
              !/[\d.\b]/.test(e.key) &&
              !["Backspace", "Delete", "ArrowLeft", "ArrowRight"].includes(
                e.key
              )
            ) {
              e.preventDefault();
            }
          }}
          max={999}
          maxLength={3}
        />
      ),
    },
    {
      title: <FormattedMessage id="Quote.UnitPrice" />,
      dataIndex: "unitPrice",
      key: "unitPrice",
      align: "center",
      width: 89,
      render: (text, record) => (
        <Input
          size="middle"
          value={`$${record.unitPrice || 0}`}
          style={{ textAlign: "right", width: "100%" }}
          maxLength={15}
          min={0}
          disabled
          onKeyDown={(e) => {
            if (
              !/[\d.\b]/.test(e.key) &&
              !["Backspace", "Delete", "ArrowLeft", "ArrowRight"].includes(
                e.key
              )
            ) {
              e.preventDefault();
            }
          }}
        />
      ),
    },
    {
      title: <FormattedMessage id="Quote.Discount" />,
      dataIndex: "discount",
      key: "discount",
      align: "center",
      width: 89,
      render: (text, record) => (
        <InputNumber
          size="middle"
          min={0}
          maxLength={4}
          controls={false}
          value={record.discount}
          style={{
            backgroundColor: "#fff7e6",
            border: "1px solid #fa8c16",
            borderRadius: 4,
            width: "100%",
            textAlign: "center",
          }}
          formatter={(value) => `${value}%`}
          parser={(value) => parseFloat(value.replace("%", ""))}
          onChange={(value) => {
            const safeValue = Math.min(100, Math.max(0, value || 0));
            handleDiscountChange(safeValue, record.key);
          }}
          onBlur={(e) => {
            const inputValue = e.target.value?.replace("%", "").trim();
            if (!inputValue || isNaN(Number(inputValue))) {
              handleDiscountChange(0, record.key);
            }
          }}
          onKeyDown={(e) => {
            if (
              !/[\d\b]/.test(e.key) &&
              !["Backspace", "Delete", "ArrowLeft", "ArrowRight"].includes(
                e.key
              )
            ) {
              e.preventDefault();
            }
          }}
        />
      ),
    },
    {
      title: <FormattedMessage id="AddEstimate.Amount" />,
      dataIndex: "amount",
      key: "amount",
      align: "right",
      width: 150,
      render: (text, record) => (
        <Input
          value={`$${record?.amount?.toFixed(2) || 0}`}
          disabled
          style={{ textAlign: "right", width: "100%" }}
        />
      ),
    },
  ];

  useEffect(() => {
    if (newProduct && currentKey !== null) {
      const matchingProduct = productsList?.items?.find(
        (prod) => prod.name === newProduct
      );

      if (matchingProduct) {
        setSelectedProductss((prevData) =>
          prevData.map((item) =>
            item.key === currentKey
              ? {
                  ...item,
                  productId: matchingProduct.id,
                  name: matchingProduct.name,
                }
              : item
          )
        );

        handleProductSelect(matchingProduct.id, currentKey);
      }

      setNewProduct(null);
      setCurrentKey(null);
    }
  }, [productsList]);

  useEffect(() => {
    if (initialProducts?.length > 0 && productsList?.items?.length > 0) {
      const formattedProducts = initialProducts.map((product) => {
        const matchedProduct = productsList.items.find(
          (p) => p.id === product.productServiceId
        );
        const unitPrice = matchedProduct?.salePrice || product.rate || 0;

        const amount = calculateAmount(
          product.quantity || 1,
          unitPrice,
          product.discount || 0
        );

        return {
          key: product.id || product.key,
          id: product.id || 0,
          productId: product.productServiceId,
          name: matchedProduct?.name || product.productServiceName || "",
          description: product.description || matchedProduct?.description || "",
          quantity: product.quantity || 1,
          unitPrice: unitPrice,
          discount: product.discount || 0,
          amount,
        };
      });

      setSelectedProductss(formattedProducts);
      setRowKey(formattedProducts.length + 1);
      calculateSubTotalAndGrandTotal(formattedProducts);
    }
  }, [initialProducts, productsList]);

  return (
    <>
      <Table
        className="estimatetable mt-20"
        bordered
        dataSource={selectedProductss}
        columns={columns}
        scroll={{ x: 1000 }}
        pagination={false}
      />
      <Drawer
        title={
          <FormattedMessage id="ProductService.AddPricingProductsServices" />
        }
        width={600}
        onClose={onClose}
        open={open}
        destroyOnClose
      >
        <AddProduct onClose={onClose} setNewProduct={setNewProduct} />
      </Drawer>
    </>
  );
}

export default ProductServiceTable;
